import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  InputAdornment,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { AsyncContent } from '~/components/AsyncContent';
import { useLoggedInEmployeeId } from '~/components/Auth';
import { EditStateActions } from '~/components/EditStateActions';
import { InstructionButtonWithDialog } from '~/components/Instructions';
import { SolaForceHeading } from '~/components/SolaForceHeading';
import { EmpBasicData } from '~/data/employee';
import { Roles } from '~/data/user';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { LegacyFieldReadMode } from '~/legacy-components/LegacyForms';
import { useSxStyles } from '~/scenes/PeopleModule/components/Tabs/BasicTab/components/EmployeeDataForm/useStyles';
import { putEmpProfileData } from '~/scenes/PeopleModule/components/Tabs/BasicTab/data';
import { employeeModulesAccessRoles } from '~/scenes/PortalViews/Views/EmployeeViews/data';
import { useHasAccessOverEmployee } from '~/services/Permissions';
import { CountryOption } from '~/types';
import { convertDateTypeDatePickerProps, getEnumName } from '~/utils/helper';

import { useEmployeeFormData } from './useEmployeeFormData';

export type IFormInput = {
  fEmpFirstName: string;
  fEmpLastName: string;
  fEmpGivenNames: string;
  fEmpGender?: string;
  fEmpDateOfBirth: Date | null;
  fEmpSocSecNum: string;
  fEmpNationality?: string;
  fEmpStatus: string;
  fEmpOrigStartDate?: Date | null;
  fEmpInfo?: string;
  fEmpPersonNumber: string;
  fEmpId: number;
  fEmpProfileId: number;
};

export const EmployeeDataForm = () => {
  const sxStyles = useSxStyles();

  const {
    empData: data,
    countryList,
    employeeStatusList,
    genderOptions,
    failedToLoad,
    isLoading,
  } = useEmployeeFormData();
  const {
    selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();

  const { hasFullAccessOverEmployee } = useHasAccessOverEmployee();
  const hasFullAccessOverEmp = hasFullAccessOverEmployee(data.fEmpId);
  const loggedInId = useLoggedInEmployeeId();

  const sortedCountryList = useMemo(() => {
    return !!countryList
      ? countryList.sort((a, b) => a.countryName.localeCompare(b.countryName))
      : [];
  }, [countryList]);

  const [editMode, setEditMode] = useState<boolean>(false);

  const ownData = +loggedInId === +data.fEmpId;
  const empViewOwnData = ownData && !editMode;

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Form validation
  const validationSchema = useMemo(() => {
    const yupStringRequired = yup.string().required(t('laFillManFields'));
    const yupDateRequired = yup
      .date()
      .required(t('laFillManFields'))
      .typeError(t('laErrorInvaDateValue'));
    const yupDateOptional = yup
      .date()
      .nullable()
      .notRequired()
      .typeError(t('laErrorInvaDateValue'));

    return yup.object().shape<IFormInput>({
      fEmpFirstName: yupStringRequired,
      fEmpLastName: yupStringRequired,
      fEmpGivenNames: yupStringRequired.test(
        'firstNameIncluded',
        t('laGivenNameValidate'),
        (value, context) => {
          return !!value ? value.includes(context.parent.fEmpFirstName) : false;
        },
      ),
      fEmpGender: yup.string(),
      fEmpDateOfBirth: yupDateRequired,
      fEmpSocSecNum: yupStringRequired,
      fEmpNationality: yup.string(),
      fEmpStatus: yup.string(),
      fEmpOrigStartDate: yupDateOptional,
      fEmpInfo: yup.string(),
      fEmpPersonNumber: yup.string(),
      fEmpId: yup.number(),
      fEmpProfileId: yup.number(),
    });
  }, [t]);
  const resolver = useYupValidationResolver(validationSchema);

  // Date field value conversion functions
  const renderDate = (date: Date | unknown): string =>
    date instanceof Date ? moment(date).format('DD.MM.YYYY').toString() : '';

  const postDateFieldValue = (
    value: Date | null | undefined,
  ): string | undefined =>
    !!value ? moment(value).format('YYYY-MM-DD').toString() : undefined;

  const initDateFieldValue = (value: string | undefined): Date | null =>
    !!value ? new Date(value) : null;

  // Setup form
  const initialFieldValues: IFormInput | object = useMemo(() => {
    if (!!data) {
      return {
        ...data,
        fEmpDateOfBirth: initDateFieldValue(data.fEmpDateOfBirth),
        fEmpOrigStartDate: initDateFieldValue(data.fEmpOrigStartDate),
      };
    }

    return {};
  }, [data]);

  const form = useForm<IFormInput>({
    mode: 'all',
    defaultValues: initialFieldValues,
    resolver: resolver as never,
  });
  const { control, reset, handleSubmit } = form;

  const restrictEdit =
    hasRoleAccessOverEmployee(
      [Roles.MANAGER, Roles.ALT_MANAGER, Roles.DEPUTY_MANAGER],
      employeeModulesAccessRoles.people.all,
    ) && !ownData;

  const isSelfEditDisabled =
    !hasRoleAccessOverEmployee([Roles.HR_ADMIN]) && ownData;

  const handleFormReset = () => {
    reset();
    setEditMode(false);
  };

  const handleFormSubmit = useMemo(() => {
    return handleSubmit(
      (formData) => {
        const postData: EmpBasicData = {
          fEmpFirstName: formData.fEmpFirstName,
          fEmpLastName: formData.fEmpLastName,
          fEmpGivenNames: formData.fEmpGivenNames,
          fEmpGender: formData.fEmpGender,
          fEmpDateOfBirth: postDateFieldValue(formData.fEmpDateOfBirth) || '',
          fEmpSocSecNum: formData.fEmpSocSecNum,
          fEmpNationality: formData.fEmpNationality,
          fEmpStatus: formData.fEmpStatus ? formData.fEmpStatus : '',
          fEmpOrigStartDate: postDateFieldValue(formData.fEmpOrigStartDate),
          fEmpInfo: formData.fEmpInfo ? formData.fEmpInfo : '',
          fEmpPersonNumber: data.fEmpPersonNumber,
          fEmpId: data.fEmpId,
          fEmpProfileId: data.fEmpProfileId,
        };

        (async () => {
          try {
            const response = await putEmpProfileData(postData, data.fEmpId);
            if (response) {
              enqueueSnackbar(t('change_saved'), {
                variant: 'success',
              });
              // setEmpData(postData);
              setEditMode(false);
            }
          } catch (error) {
            enqueueSnackbar(t('errorMsg'), {
              variant: 'error',
            });
          }
        })();
      },
      (errors) => {
        // TODO: Create better & generic form validation error alert/dialog/popup thing
        // console.log(errors, 'handleSubmit errors');
        Object.values(errors).forEach((fieldError) => {
          enqueueSnackbar(fieldError.message, {
            variant: 'error',
          });
        });
      },
    );
  }, [data, enqueueSnackbar, handleSubmit, t]);

  // Form field value utilities
  const getGenderName = (value: string): string => {
    return genderOptions.find((g) => g.value === value)?.label || '';
  };

  const getCountryOption = (countryCode: string): CountryOption | undefined =>
    sortedCountryList.find((c) => c.countryCode === countryCode);

  const getCountryName = (countryCode: string): string =>
    getCountryOption(countryCode)?.countryName || '';

  const getEmployeeState = (statusCode: string): string => {
    return !!employeeStatusList
      ? getEnumName(employeeStatusList, statusCode)
      : statusCode;
  };

  const loadingComplete = !isLoading && !failedToLoad;

  return (
    <>
      <form onSubmit={(e) => e.preventDefault()}>
        <Box>
          <SolaForceHeading heading={t('laPersonalData')}>
            <EditStateActions
              canEdit={!restrictEdit}
              readMode={!editMode}
              onEdit={() => setEditMode(true)}
              onSave={handleFormSubmit}
              onCancel={handleFormReset}
              slotProps={{
                edit: {
                  'data-testid': 'editButton',
                  disabled: !loadingComplete,
                },
                save: {
                  'data-testid': 'formSaveButton',
                },
                cancel: {
                  'data-testid': 'formCancelButton',
                },
              }}
            />
          </SolaForceHeading>
        </Box>
        <AsyncContent
          isLoading={isLoading}
          failedToLoad={failedToLoad}
          failedToLoadMessage={t('laFailedToLoad')}
        >
          <Box sx={editMode ? sxStyles.editModeLayout : undefined}>
            <Controller
              name="fEmpFirstName"
              control={control}
              defaultValue=""
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => {
                const label = t('fEmpFirstName');
                if (!editMode) {
                  return <LegacyFieldReadMode label={label} value={value} />;
                }

                return (
                  <TextField
                    label={label}
                    value={value}
                    onChange={onChange}
                    required={true}
                    error={!!error}
                    helperText={error?.message}
                    fullWidth={true}
                    size={'small'}
                    data-testid="firstName"
                  />
                );
              }}
            />
            <Controller
              name="fEmpGivenNames"
              control={control}
              defaultValue=""
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => {
                const label = t('fEmpGivenNames');
                if (!editMode) {
                  return <LegacyFieldReadMode label={label} value={value} />;
                }

                return (
                  <TextField
                    label={label}
                    value={value}
                    onChange={onChange}
                    required={true}
                    error={!!error}
                    helperText={error?.message}
                    fullWidth={true}
                    size={'small'}
                    data-testid="givenName"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <InstructionButtonWithDialog
                            instruction={{
                              title: `${t('laInstruction')}: ${t(
                                'fEmpGivenNames',
                              )}`,
                              content: t('laGivenNameValidate'),
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                );
              }}
            />
            <Controller
              name="fEmpLastName"
              control={control}
              defaultValue=""
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => {
                const label = t('fEmpLastName');
                if (!editMode) {
                  return <LegacyFieldReadMode label={label} value={value} />;
                }

                return (
                  <TextField
                    label={label}
                    value={value}
                    onChange={onChange}
                    required={true}
                    error={!!error}
                    helperText={error?.message}
                    fullWidth={true}
                    size={'small'}
                    data-testid="lastName"
                  />
                );
              }}
            />
            <Controller
              name="fEmpGender"
              control={control}
              defaultValue=""
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => {
                const label = t('fEmpGender');
                if (!editMode) {
                  return (
                    <LegacyFieldReadMode
                      label={label}
                      value={getGenderName(value || '')}
                      data-testid="genderField"
                    />
                  );
                }

                return (
                  <FormControl
                    component="fieldset"
                    error={!!error}
                    size={'small'}
                  >
                    <FormLabel component="legend">{label}</FormLabel>
                    <FormGroup aria-label="position" row>
                      {genderOptions.map((option, index) => {
                        return (
                          <FormControlLabel
                            key={`gender-${option.value}-${index}`}
                            label={option.label}
                            value={option.value}
                            control={
                              <Checkbox
                                checked={option.value === value}
                                onChange={(_event, checked) => {
                                  onChange(checked ? option.value : '');
                                }}
                              />
                            }
                            labelPlacement={'end'}
                          />
                        );
                      })}
                    </FormGroup>
                    <FormHelperText>{error?.message}</FormHelperText>
                  </FormControl>
                );
              }}
            />
            {(hasFullAccessOverEmp || empViewOwnData) && (
              <>
                <Controller
                  name="fEmpDateOfBirth"
                  control={control}
                  defaultValue={null}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => {
                    const label = t('fEmpDateOfBirth');
                    if (!editMode) {
                      return (
                        <LegacyFieldReadMode
                          label={label}
                          value={renderDate(value)}
                        />
                      );
                    }

                    return (
                      <DatePicker
                        {...convertDateTypeDatePickerProps({
                          value,
                          onChange,
                        })}
                        label={label}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            error: !!error,
                            helperText: error?.message,
                            required: true,
                          },
                        }}
                        data-testid="dateOfBirth"
                        disabled={isSelfEditDisabled}
                      />
                    );
                  }}
                />
                <Controller
                  name="fEmpSocSecNum"
                  control={control}
                  defaultValue=""
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => {
                    const label = t('fEmpSocSecNum');
                    if (!editMode) {
                      return (
                        <LegacyFieldReadMode label={label} value={value} />
                      );
                    }

                    return (
                      <TextField
                        label={label}
                        value={value}
                        onChange={onChange}
                        required={true}
                        error={!!error}
                        helperText={error?.message}
                        fullWidth={true}
                        size={'small'}
                        data-testid="socialSecurityNumber"
                        disabled={isSelfEditDisabled}
                      />
                    );
                  }}
                />
                <Controller
                  name="fEmpNationality"
                  control={control}
                  defaultValue=""
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => {
                    const label = t('fEmpNationality');
                    if (!editMode) {
                      return (
                        <LegacyFieldReadMode
                          label={label}
                          value={getCountryName(value || '')}
                        />
                      );
                    }

                    return (
                      <Autocomplete
                        fullWidth={true}
                        size={'small'}
                        options={sortedCountryList}
                        getOptionLabel={(c) => c.countryName}
                        isOptionEqualToValue={(o, v) =>
                          o.countryCode === v.countryCode
                        }
                        onChange={(_event, value) => {
                          onChange(value?.countryCode || '');
                        }}
                        value={getCountryOption(value || '') || null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={label}
                            error={!!error?.message}
                            helperText={error?.message}
                          />
                        )}
                        disabled={isSelfEditDisabled}
                        data-testid="nationalityField"
                      />
                    );
                  }}
                />
                <Controller
                  name="fEmpStatus"
                  control={control}
                  defaultValue=""
                  render={({ field: { onChange, value } }) => {
                    const label = t('fEmpStatus');
                    if (!editMode) {
                      return (
                        <LegacyFieldReadMode
                          label={label}
                          value={getEmployeeState(value)}
                        />
                      );
                    }

                    return (
                      <TextField
                        label={label}
                        value={value}
                        disabled={isSelfEditDisabled}
                        onChange={onChange}
                        hidden
                      />
                    );
                  }}
                />
                <Controller
                  name="fEmpOrigStartDate"
                  control={control}
                  defaultValue={null}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => {
                    const label = t('laEmpOrigStartDate');
                    if (!editMode) {
                      return (
                        <LegacyFieldReadMode
                          label={label}
                          value={renderDate(value)}
                        />
                      );
                    }

                    return (
                      <DatePicker
                        {...convertDateTypeDatePickerProps({
                          value,
                          onChange,
                        })}
                        label={label}
                        slotProps={{
                          field: {
                            clearable: true,
                          },
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            error: !!error,
                            helperText: error?.message,
                          },
                        }}
                        data-testid="originalStartDate"
                        disabled={isSelfEditDisabled}
                      />
                    );
                  }}
                />
                <Controller
                  name="fEmpInfo"
                  control={control}
                  defaultValue=""
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => {
                    const label = t('fEmpInfo');
                    if (!editMode) {
                      return (
                        <LegacyFieldReadMode
                          label={label}
                          value={value || ''}
                        />
                      );
                    }

                    return (
                      <TextField
                        label={label}
                        value={value}
                        onChange={onChange}
                        error={!!error}
                        helperText={error?.message}
                        fullWidth={true}
                        size={'small'}
                        data-testid="infoField"
                        disabled={isSelfEditDisabled}
                        multiline={true}
                        minRows={3}
                      />
                    );
                  }}
                />
              </>
            )}
          </Box>
        </AsyncContent>
      </form>
    </>
  );
};
